/**
 * #416 — which live feeds are degraded, as data.
 *
 * Every /web route destructures only `data` from useFetch and drops `error`.
 * useFetch initializes data to [] and never calls setData on failure, so a
 * dead feed and a healthy-but-quiet feed render identically. On a hazard
 * console that ambiguity is the dangerous one: the operator's correct
 * reading of an empty INTEL rail is "nothing is happening".
 *
 * Two distinctions this model has to get right:
 *
 * 1. Unauthenticated is not degraded. useFetch deliberately skips the mount
 *    fetch without a session (#354) — it returns [] with error null. Raising
 *    an alarm there would train the operator to ignore the indicator.
 *
 * 2. Demo data masks the failure. Under REACT_APP_DEMO_DATA=1 the routes
 *    merge demo watches into the live ones, so a dead feed still renders a
 *    populated, plausible rail. The indicator is driven by feed state, never
 *    by whether the list looks empty, so it fires either way.
 */

/**
 * @param {Array<{name:string, error?:*, loading?:boolean}>} feeds
 * @returns {{degraded: Array<{name:string}>, isDegraded: boolean}}
 *   `degraded` preserves input order; malformed entries are skipped.
 */
export function summarizeFeedStatus(feeds) {
  const list = Array.isArray(feeds) ? feeds : [];
  const degraded = [];
  for (const feed of list) {
    if (!feed || typeof feed !== "object") continue;
    if (typeof feed.name !== "string" || !feed.name.trim()) continue;
    // A feed still in flight has not failed yet — a spinner-then-alarm flash
    // on every page load would be worse than no indicator.
    if (feed.loading === true) continue;
    if (!feed.error) continue;
    degraded.push({ name: feed.name.trim() });
  }
  return { degraded, isDegraded: degraded.length > 0 };
}
